import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { Globe, Target, FileText, Languages } from "lucide-react";
import AnimatedCounter from "../components/AnimatedCounter";
import { fadeInUp, staggerContainer } from "../utils/animations";

const stats = [
  {
    icon: Globe,
    value: "25",
    suffix: "+",
    label: "Countries",
    description: "Global footprint across healthcare networks",
    color: "#C6605F",
    bgColor: "rgba(198, 96, 95, 0.1)",
  },
  {
    icon: Target,
    value: "99.9",
    suffix: "%",
    label: "Accuracy",
    description: "Proven precision in report interpretation",
    color: "#3CD0FF",
    bgColor: "rgba(60, 208, 255, 0.1)",
  },
  {
    icon: FileText,
    value: "1",
    suffix: "M+",
    label: "Smart Reports",
    description: "Delivered to patients & providers",
    color: "#3567D6",
    bgColor: "rgba(53, 103, 214, 0.1)",
  },
  {
    icon: Languages,
    value: "15",
    suffix: "",
    label: "Languages",
    description: "Inclusive multilingual user experience",
    color: "#369868",
    bgColor: "rgba(54, 152, 104, 0.1)",
  },
];

const StatsSection = () => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >

      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
        {/* Section Title */}
        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          sx={{ textAlign: "center", mb: { xs: 4, sm: 5, md: 6 } }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "36px", md: "48px" },
              fontWeight: 700,
              color: "#1D47A4",
              mb: 2,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            We believe in numbers
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Grid
          container
          spacing={{ xs: 2, sm: 2.5, md: 3 }}
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <Box
                  component={motion.div}
                  variants={fadeInUp}
                  sx={{
                    textAlign: "center",
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: { xs: "16px", md: "20px" },
                    background: "#ffffff",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                      borderColor: stat.color,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: { xs: "48px", sm: "52px", md: "56px" },
                      height: { xs: "48px", sm: "52px", md: "56px" },
                      borderRadius: { xs: "12px", md: "14px" },
                      backgroundColor: stat.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: { xs: 1.5, sm: 2 },
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <IconComponent 
                      size={24} 
                      color={stat.color} 
                      strokeWidth={2}
                      style={{ 
                        width: "24px", 
                        height: "24px" 
                      }}
                    />
                  </Box>

                  {/* Animated Counter */}
                  <AnimatedCounter
                    end={stat.value}
                    suffix={stat.suffix}
                    duration={2.5}
                    sx={{
                      fontSize: { xs: "28px", sm: "32px", md: "36px" },
                      fontWeight: 700,
                      color: "#3567D6",
                      mb: { xs: 0.5, sm: 0.75 },
                      fontFamily: "'Poppins', sans-serif",
                      display: "block",
                      position: "relative",
                      zIndex: 1,
                      lineHeight: 1.2,
                    }}
                  />

                  {/* Label */}
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#1D47A4",
                      mb: { xs: 0.5, sm: 0.75 },
                      fontSize: { xs: "14px", sm: "15px", md: "16px" },
                      fontFamily: "'Poppins', sans-serif",
                      position: "relative",
                      zIndex: 1,
                      lineHeight: 1.3,
                    }}
                  >
                    {stat.label}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      fontSize: { xs: "11px", sm: "12px", md: "13px" },
                      color: "#7F7F7F",
                      lineHeight: 1.4,
                      fontFamily: "'Poppins', sans-serif",
                      position: "relative",
                      zIndex: 1,
                      mt: "auto",
                    }}
                  >
                    {stat.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default StatsSection;